import { Injectable, ElementRef } from "@angular/core";

import {
  OrderDataActionPromiseService,
  OrderDataPromiseLookupService,
  FillOrderData,
  FillTolerancePromiseService,
  InterventionDataPromiseLookupService,
  OrderSlippageService,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { NotificationService } from "../../../shared/cubiq-services/lookup/notification/impl/NotificationService";
import { FillOrdersModel } from "../models/FillOrdersModel";
import { Subscription, Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../../bfx-dialogs/models/bfx-dialog-command.enum";

import { OrderFillResultImpl } from "shared/cubiq-model-multi-asset";
import { BFXNotificationType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationSubType";
import { BFXNotificationEventType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationEventType";
import { DetailFillCcyPair } from "../../../shared/cubiq-services/action/impl/DetailFillCcyPair";
import { BfxFillWarningService } from "../../bfx-common/services/bfx-fill-warning.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxDetailFillService {
  private fillOrdersModel: FillOrdersModel;
  private fillActionSubscription: Subscription;
  private selectedTabIdx: number = 0;
  private fillOrdersOwnerDocumentWeakMap: WeakMap<FillOrderData[], Document> = new WeakMap();

  /**********************************************************************/
  /* OBSERVABLE */
  // Observable string sources
  private orderActionDialogSource = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.orderActionDialogSource.asObservable();

  public dataUpdatedSubject = new Subject<DetailFillCcyPair[]>();

  public fillResultErrorSubject = new Subject<OrderFillResultImpl>();
  public fillResultError$ = this.fillResultErrorSubject.asObservable();

  constructor(
    private orderDataActionService: OrderDataActionPromiseService,
    private orderDataService: OrderDataPromiseLookupService,
    private notifService: NotificationService,
    private translationService: TranslateService,
    private fillToleranceService: FillTolerancePromiseService,
    private interventionDataPrimaryService: InterventionDataPromiseLookupService,
    private orderSlippageService: OrderSlippageService,
    private fillWarningService: BfxFillWarningService,
  ) {
    orderDataService.getModelDetailFillSubject().subscribe(this.onDetailFillEvent.bind(this));
  }

  registerFillOrdersOwnerDocument(fillOrders: FillOrderData[], ownerDocument: Document) {
    this.fillOrdersOwnerDocumentWeakMap.set(fillOrders, ownerDocument);
  }

  /**
   * bfx-detail-fill.component is a singleton popout that
   * uses this ref *directly* until it is closed
   */
  getFillOrdersModel(): FillOrdersModel {
    return this.fillOrdersModel;
  }

  lockOrdersForFill(orders: Array<CubiqTS.OrderDataImpl>): Promise<boolean> {
    return new Promise<any>((resolve, reject) => {
      this.orderDataActionService.lockOrdersForFill(orders).then((values) => {
        let alreadyOpen: boolean =
          this.fillOrdersModel &&
          this.fillOrdersModel.getAllTabIds() &&
          this.fillOrdersModel.getAllTabIds().length > 0;
        if (alreadyOpen) {
          this.fillOrdersModel.addRows(values);
          this.dataUpdatedSubject.next(values);
        } else {
          if (this.fillActionSubscription) {
            this.fillActionSubscription.unsubscribe();
          }
          this.fillOrdersModel = new FillOrdersModel(values, this.orderSlippageService);
          this.fillActionSubscription = this.fillOrdersModel.fillActionSubject.subscribe(
            this.fillFromDropDown.bind(this),
          );
        }
        this.orderDataService.setDetailFillIds(this.fillOrdersModel.getAllTabIds());
        resolve(!alreadyOpen);
      });
    });
  }

  public fillFromDropDown(label: string, element: Element) {
    switch (label) {
      case "Fill":
        this.fillSelected(element);
        break;
      case "Fill TP":
        this.fillSelectedTp(element);
        break;
      case "Fill SL":
        this.fillSelectedSl(element);
        break;
    }
  }
  unlockOrders(ids: Array<string>): void {
    this.orderDataActionService.unlockOrdersFromFill(ids);
  }

  tabIndexChange(index: number): void {
    this.selectedTabIdx = index;
    this.fillOrdersModel.tabIndexChange(index);
  }

  protected onDetailFillEvent(ids: any): void {
    if (ids instanceof Array) {
      ids.forEach((element) => {
        if (this.fillOrdersModel.deleteRowById(element.id)) {
          // this wasnt expected so we need to send the notification for unlock here
          // this.notificationService.onDetailFillUnlockEvent({orderId: element.id, type: BFXNotificationType.UNLOCK, subType:BFXNotificationSubType.DETAIL_FILL_UNLOCK, lockUser:element.lockUser});
          this.notifService.fireNotificationEvent(
            this.notifService.detailFillUnlockDataToNotification(
              element.id,
              BFXNotificationType.UNLOCK,
              BFXNotificationSubType.DETAIL_FILL_UNLOCK,
              element.lockUser,
              element.orderRef,
            ),
            BFXNotificationEventType.DETAIL_FILL_UNLOCK_EVENT,
          );
        }
      });
    } else {
      if (this.fillOrdersModel.deleteRowById(ids.id)) {
        // this wasnt expected so we need to send the notification for unlock here
        // this.notificationService.onDetailFillUnlockEvent({orderId: ids.id, type: BFXNotificationType.UNLOCK, subType:BFXNotificationSubType.DETAIL_FILL_UNLOCK, lockUser:ids.lockUser});
        this.notifService.fireNotificationEvent(
          this.notifService.detailFillUnlockDataToNotification(
            ids.id,
            BFXNotificationType.UNLOCK,
            BFXNotificationSubType.DETAIL_FILL_UNLOCK,
            ids.lockUser,
            ids.orderRef,
          ),
          BFXNotificationEventType.DETAIL_FILL_UNLOCK_EVENT,
        );
      }
    }
  }

  public fillSelected(element: Element) {
    let fillOrders: Array<FillOrderData> = this.fillOrdersModel.getSelectedFills();
    this.registerFillOrdersOwnerDocument(fillOrders, this.fillOrdersModel.ownerDocument);
    if (fillOrders.length > 0) {
      this.checkForTolerance(fillOrders);
    }
  }

  public fillSelectedTp(element: Element) {
    let fillOrders: Array<FillOrderData> = this.fillOrdersModel.getSelectedTPFills();
    this.registerFillOrdersOwnerDocument(fillOrders, this.fillOrdersModel.ownerDocument);
    if (fillOrders.length > 0) {
      this.checkForTolerance(fillOrders);
    }
  }
  public fillSelectedSl(element: Element) {
    let fillOrders: Array<FillOrderData> = this.fillOrdersModel.getSelectedSLFills();
    this.registerFillOrdersOwnerDocument(fillOrders, this.fillOrdersModel.ownerDocument);
    if (fillOrders.length > 0) {
      this.checkForTolerance(fillOrders);
    }
  }

  private continueDetailFill(fillOrders: Array<FillOrderData>): void {
    const a = fillOrders.slice();
    this.orderDataActionService.detailFillOrders(fillOrders).then((responses) => {
      // We're gonna unlock anything that was fiolled or failed.
      // we need the erros for the dialog

      let messages: string[] = [];

      let results: Array<OrderFillResultImpl> = responses;
      if (results) {
        console.log(a);
        results.forEach(async (result) => {
          this.fillOrdersModel.removeFromQueue(result.orderId);

          if (
            result.errors != null &&
            result.errors.errorMessages != null &&
            result.errors.errorMessages.length > 0
          ) {
            const orderData = await this.orderDataService.getOrderData(result.orderId);
            const orderRef = orderData?.orderRef ?? result.orderId;
            result.errors.errorMessages.forEach((errorMsg) => {
              let errorObject: any = JSON.parse(errorMsg + "");
              messages.push(this.translationService.instant(errorObject.id, errorObject.args));
            });
            this.fillResultErrorSubject.next(result);
          }
        });
      }

      if (messages.length > 0) {
        let payload = {
          title: "ordact.fill.failed",
          messages: messages,
          callback: () => {},
          callbackNegative: () => {},
        };

        this.orderActionDialogSource.next(
          new BfxDialogData(
            BfxDialogCommand.DIALOG_ORDER_FILL_FAIL,
            null,
            fillOrders,
            payload,
            this.fillOrdersOwnerDocumentWeakMap.has(fillOrders)
              ? this.fillOrdersOwnerDocumentWeakMap.get(fillOrders).body
              : document.body,
          ),
        );
      }
    });
  }

  private revertDetailFill(fillOrders: Array<FillOrderData>): void {
    let idsToUnlock: Array<string> = fillOrders.map((fillOrder) => fillOrder.orderId);
    idsToUnlock.forEach((id) => {
      this.fillOrdersModel.removeFromQueue(id);
    });
  }

  private async checkForTolerance(orders: Array<FillOrderData>): Promise<void> {
    let errorMessages: string[] = [];
    let failOnly: boolean = false;
    // if we have component legs - check if they have zero(0) marketSpots
    // if so then we add erroes for those.
    // and indicate that the dialog is failonly
    orders.forEach((order) => {
      if (order.crosses != null && order.crosses.length > 0) {
        order.crosses.forEach((cross) => {
          if (cross.mktSpot == null || Number(cross.mktSpot) === 0) {
            let errorMsg: string = this.translationService.instant("NO_COMPONENT_RATE", [
              order.orderRef,
            ]);
            errorMessages.push(errorMsg);
            failOnly = true;
          }
        });
      }
    });

    const valueDateMessages = this.fillWarningService.buildValueDateMessages(orders);
    const tenorAdjustedMessages = await this.fillWarningService.buildTenorAdjustedMessages(orders);

    this.fillToleranceService.checkDetailFillTolerance(orders).then((response) => {
      if (
        valueDateMessages.length === 0 &&
        tenorAdjustedMessages.length === 0 &&
        errorMessages.length == 0 &&
        response.failedIds.length == 0
      ) {
        this.continueDetailFill(orders);
      } else {
        let payload: any;
        if (response.failedIds.length === 1) {
          errorMessages.push(response.failedIds[0].error);
          if (!failOnly) {
            errorMessages = this.fillWarningService.addFillValueDateErrorMessage(
              valueDateMessages,
              errorMessages,
            );
            errorMessages = this.fillWarningService.addErrorMessages(
              tenorAdjustedMessages,
              errorMessages,
            );
          }
        } else if (response.failedIds.length > 1) {
          errorMessages.push(
            this.translationService.instant("tolerance.warning.multiple.message.1", [
              response.failedIds.length,
            ]),
          );

          if (!failOnly) {
            errorMessages = this.fillWarningService.addFillValueDateErrorMessage(
              valueDateMessages,
              errorMessages,
            );
            errorMessages = this.fillWarningService.addErrorMessages(
              tenorAdjustedMessages,
              errorMessages,
            );
          }

          errorMessages.push(
            this.translationService.instant("tolerance.warning.multiple.message.2", [
              orders.length,
            ]),
          );

          errorMessages.push(this.fillWarningService.addOrderRefs(response.origRequest));
        } else if (!failOnly) {
          errorMessages = this.fillWarningService.addFillValueDateErrorMessage(
            valueDateMessages,
            errorMessages,
          );
          errorMessages = this.fillWarningService.addErrorMessages(
            tenorAdjustedMessages,
            errorMessages,
          );
        }

        payload = {
          title: failOnly ? "ordact.fill.failed" : "tolerance.warning.title",
          messages: errorMessages,
          callback: failOnly
            ? this.revertDetailFill.bind(this, orders)
            : this.continueDetailFill.bind(this, orders),
          callbackNegative: this.revertDetailFill.bind(this, orders),
        };

        this.orderActionDialogSource.next(
          new BfxDialogData(
            failOnly
              ? BfxDialogCommand.DIALOG_ORDER_FILL_FAIL
              : BfxDialogCommand.DIALOG_ORDER_FILL_TOLERANCE_WARNING_OPEN,
            null,
            orders,
            payload,
            this.fillOrdersOwnerDocumentWeakMap.has(orders)
              ? this.fillOrdersOwnerDocumentWeakMap.get(orders).body
              : document.body,
          ),
        );
      }
    });
    return null;
  }
}
